import { RuleId } from '@wix/a11y-rules';
import { createRuleData } from '@wix/ambassador-promote-accessibility-v1-accessibility-report/http';
import { RuleAction } from '@wix/ambassador-promote-accessibility-v1-accessibility-report/types';
import { Text } from '@wix/design-system';
import { useErrorHandler, useHttpClient, useTranslation } from '@wix/fe-essentials/react';
import { FixerType, RuleListStatus, ToggleType } from '@wix/rules-panel-ui';
import { useAtom } from 'jotai';
import React, { useCallback } from 'react';
import { currentTaskInfoAtom } from '../../../atoms/bi-atoms';
import { BiCtaNames, BiModalTypes } from '../../../bi/types';
import { convertScanStatusToUiStatus } from '../../../components/fixers/alt-image-fixer/utils';
import { showErrorModal } from '../../../components/modals/show-error-modal';
import { RuleDescriptionComponent } from '../../../components/rule-description-component';
import { usePanelStateAtom } from '../../../hooks/rules-state';
import { PageCompletionStatus } from '../../../hooks/rules-state/rule-state-mapper';
import { useA11yWizardAdapter } from '../../../hooks/use-a11y-wizard-adapter';
import { useBiLogger } from '../../../hooks/use-bi-logger';
import { useNavigation } from '../../../hooks/use-navigation';
import { useOpenArticleByEditorHost } from '../../../hooks/use-open-article-by-editor-host';
import { useSessionInfoContext } from '../../../hooks/use-session-info-context';
import { getCompRefType } from '../../../utils/get-comp-ref-type';
import { isEditor3Host } from '../../../utils/get-editor-host';
import { getFixerDesignProps, getFooterButtonTypes } from '../../../utils/get-fixer-design-props';
import { getPageTypeForBi } from '../../shared/bi-helpers';
import { shouldUpdateRuleStatus } from '../../shared/helpers';
import { AffectedContainersList } from './affected-containers-list';
import { getPageAutoDomaOrderRadioOptions, PAGE_AUTO_DOM_ORDER_FIX_OPTION } from './radio-options';
export const usePageAutoDomOrderRule = () => {
  const {
    t
  } = useTranslation();
  const {
    a11yWizardAdapter,
    editorHost
  } = useA11yWizardAdapter();
  const navigateToPage = useNavigation();
  const {
    openSelectedArticle
  } = useOpenArticleByEditorHost();
  const {
    getRuleState,
    getGroupData,
    setRuleState
  } = usePanelStateAtom();
  const biLogger = useBiLogger();
  const [currentTaskInfo, setCurrentTaskInfo] = useAtom(currentTaskInfoAtom);
  const httpClient = useHttpClient();
  const {
    withErrorHandler
  } = useErrorHandler();
  const {
    branchId
  } = useSessionInfoContext();
  const handleElementClick = useCallback((element, elementCount, failedElementCount) => {
    const compRef = {
      id: element.id,
      type: getCompRefType(editorHost)
    };
    biLogger.taskCtaClick({
      ctaName: BiCtaNames.View,
      elementCount,
      failedElementCount
    });
    if (compRef) {
      a11yWizardAdapter.selectComponent(compRef);
    } else {
      console.error('Failed to get component reference');
    }
  }, [a11yWizardAdapter, editorHost, biLogger]);
  const getErrorPageAutoDomOrderModalProps = useCallback(() => ({
    content: /*#__PURE__*/React.createElement(Text, null, t('accessibility-wizard.rules-list.page-auto-dom-order.error-modal.content')),
    primaryButtonText: t('accessibility-wizard.rules-list.page-auto-dom-order.error-modal.primary-button'),
    secondaryButtonText: t('accessibility-wizard.rules-list.page-auto-dom-order.error-modal.secondary-button'),
    theme: 'destructive',
    title: t('accessibility-wizard.rules-list.page-auto-dom-order.error-modal.title')
  }), [t]);
  const getPageAutoDomOrderRule = useCallback(_ref => {
    var _ruleState$state, _ruleState$state2, _ruleState$state3, _ruleState$state4;
    let {
      ruleExecutionResult,
      pageId,
      routerId
    } = _ref;
    if (!pageId) {
      throw new Error('pageId is required for PAGE_AUTO_DOM_ORDER rule');
    }
    if (isEditor3Host(editorHost) === false) {
      return null;
    }
    const {
      result,
      ruleId
    } = ruleExecutionResult;
    const {
      status,
      failedElements
    } = result;
    if (status === 'not-applicable') {
      return null;
    }
    const failedComponents = (failedElements || []).map(component => {
      const compRef = {
        id: component.id,
        type: getCompRefType(editorHost)
      };
      const componentData = a11yWizardAdapter.getComponent(compRef);
      return {
        ...component,
        semanticName: componentData == null ? void 0 : componentData.label
      };
    });
    const ruleState = getRuleState(pageId, ruleId);
    const {
      analysisState,
      itemType
    } = getGroupData(pageId) ?? {};
    const ruleStatus = failedComponents.length === 0 ? RuleListStatus.passed : status;
    const uiStatus = (ruleState == null || (_ruleState$state = ruleState.state) == null ? void 0 : _ruleState$state.status) ?? convertScanStatusToUiStatus(ruleStatus);
    if (shouldUpdateRuleStatus(analysisState, uiStatus, ruleState, routerId)) {
      setRuleState(pageId, ruleId, {
        status: uiStatus
      });
    }
    const elementCount = failedComponents.length || 1;
    const failedElementCount = uiStatus === RuleListStatus.failed ? elementCount : 0;
    const onApply = async option => {
      const handleTryAgain = async fixOption => {
        biLogger.taskCtaClick({
          ctaName: BiCtaNames.TryAgain,
          elementCount,
          failedElementCount
        });
        await onApply(fixOption);
      };
      const handleCancel = () => {
        biLogger.taskCtaClick({
          ctaName: BiCtaNames.Cancel,
          elementCount,
          failedElementCount
        });
      };
      biLogger.taskElementCtaClick({
        ctaName: BiCtaNames.Apply,
        index: 0,
        status: uiStatus
      });
      try {
        if (option.value === PAGE_AUTO_DOM_ORDER_FIX_OPTION.AUTO) {
          failedComponents.forEach(component => {
            const compRef = {
              id: component.id,
              type: getCompRefType(editorHost)
            };
            a11yWizardAdapter.enableComponentAutoDOMOrder(compRef);
          });
          setRuleState(pageId, ruleId, {
            completionStatus: PageCompletionStatus.AutomaticCompleted,
            status: RuleListStatus.passed
          });
        } else {
          const createRuleDataPromises = failedComponents.map(component => withErrorHandler(async () => httpClient.request(createRuleData({
            ruleData: {
              branchId: branchId || 'master',
              elementId: component.id,
              pageId,
              ruleAction: RuleAction.COMPLETED_MANUALLY,
              ruleId: RuleId.PAGE_AUTO_DOM_ORDER,
              userSelectionRuleData: {
                pageAutoDomOrderRuleData: {
                  isManuallyHandled: true
                }
              }
            }
          })), {
            errorCodesMap: {}
          }));
          await Promise.all(createRuleDataPromises);
          setRuleState(pageId, ruleId, {
            completionStatus: PageCompletionStatus.ManualCompleted,
            status: RuleListStatus.passed
          });
        }
        biLogger.taskElementApplyClick({
          elementIndexList: failedComponents.map((_, index) => index),
          elementStatusList: failedComponents.map(() => RuleListStatus.passed)
        });
        biLogger.taskStatusChange({
          elementCount,
          failedElementCount: 0,
          status: RuleListStatus.passed
        });
      } catch (error) {
        console.error('Error applying page auto DOM order fix:', error);
        biLogger.modalView({
          modalType: BiModalTypes.UnableToOrganizeDOMOrderPage
        });
        await showErrorModal({
          ...getErrorPageAutoDomOrderModalProps(),
          a11yWizardAdapter,
          editorHost,
          primaryButtonOnClick: () => handleTryAgain(option),
          secondaryButtonOnClick: handleCancel
        });
      }
    };
    const radioOptions = getPageAutoDomaOrderRadioOptions(t);
    const radioGroupLabel = t('accessibility-wizard.rules-list.page-auto-dom-order.organize-dom-order');
    const completedRuleContent = {
      editButtonLabel: t('accessibility-wizard.common.edit'),
      inputTitle: radioGroupLabel,
      title: t('accessibility-wizard.rules-list.issue-resolved'),
      value: t('accessibility-wizard.rules-list.page-auto-dom-order.completed-value.manually-organized', {
        count: failedComponents.length
      })
    };
    const defaultValue = uiStatus === RuleListStatus.failed ? undefined : (ruleState == null || (_ruleState$state2 = ruleState.state) == null ? void 0 : _ruleState$state2.completionStatus) === PageCompletionStatus.ManualCompleted ? radioOptions.find(option => option.value === PAGE_AUTO_DOM_ORDER_FIX_OPTION.MANUAL) : (ruleState == null || (_ruleState$state3 = ruleState.state) == null ? void 0 : _ruleState$state3.completionStatus) === PageCompletionStatus.AutomaticCompleted ? radioOptions.find(option => option.value === PAGE_AUTO_DOM_ORDER_FIX_OPTION.AUTO) : undefined;

    // Create description based on failed elements
    const getDescription = () => {
      if (uiStatus === RuleListStatus.passed) {
        const {
          completionStatus
        } = (ruleState == null ? void 0 : ruleState.state) ?? {};
        if (completionStatus === PageCompletionStatus.ManualCompleted) {
          return /*#__PURE__*/React.createElement(RuleDescriptionComponent, {
            i18nKey: "accessibility-wizard.rules-list.page-auto-dom-order.description.passed.manually",
            openArticle: () => {
              openSelectedArticle(editorHost, 'editor-package-accessibility-wizard.articles-page-dom-order-help');
              biLogger.taskCtaClick({
                ctaName: BiCtaNames.LearnMore,
                elementCount,
                failedElementCount
              });
            }
          });
        }
        return /*#__PURE__*/React.createElement(RuleDescriptionComponent, {
          i18nKey: "accessibility-wizard.rules-list.page-auto-dom-order.description.passed.automatically",
          openArticle: () => {
            openSelectedArticle(editorHost, 'editor-package-accessibility-wizard.articles-page-dom-order-help');
            biLogger.taskCtaClick({
              ctaName: BiCtaNames.LearnMore,
              elementCount,
              failedElementCount
            });
          }
        });
      }

      // If rule has failed, show a single failure message regardless of count
      return /*#__PURE__*/React.createElement(RuleDescriptionComponent, {
        i18nKey: "accessibility-wizard.rules-list.page-auto-dom-order.description.failed",
        openArticle: () => {
          openSelectedArticle(editorHost, 'editor-package-accessibility-wizard.articles-page-dom-order-help');
          biLogger.taskCtaClick({
            ctaName: BiCtaNames.LearnMore,
            elementCount,
            failedElementCount
          });
        }
      });
    };
    return {
      description: getDescription(),
      elementCount,
      failedElementCount,
      fixer: FixerType.RadioGroupRule,
      fixerProps: {
        completedRuleContent,
        defaultValue,
        design: getFixerDesignProps(editorHost),
        failedElementsComponent: /*#__PURE__*/React.createElement(AffectedContainersList, {
          failedElements: failedComponents,
          onContainerClick: () => {
            biLogger.taskCtaClick({
              ctaName: BiCtaNames.FailedElementsContainer,
              elementCount,
              failedElementCount
            });
          },
          onElementClick: element => handleElementClick(element, elementCount, failedElementCount)
        }),
        footerLabels: {
          buttonTypes: getFooterButtonTypes(editorHost),
          dismiss: t('accessibility-wizard.rules-list.page-auto-dom-order.cancel'),
          submit: t('accessibility-wizard.rules-list.page-auto-dom-order.apply')
        },
        onApply,
        onCancel: () => {
          biLogger.taskCtaClick({
            ctaName: BiCtaNames.Cancel,
            elementCount,
            failedElementCount
          });
        },
        radioGroupLabel,
        radioOptions
      },
      id: `${routerId ?? pageId}-${ruleId}`,
      isEditableOnceCompleted: (ruleState == null || (_ruleState$state4 = ruleState.state) == null ? void 0 : _ruleState$state4.completionStatus) === PageCompletionStatus.ManualCompleted,
      label: t('accessibility-wizard.rules-list.page-auto-dom-order.title'),
      onRuleToggle: function (toggleType, ruleIndex, initiatedByUser) {
        if (initiatedByUser === void 0) {
          initiatedByUser = false;
        }
        setCurrentTaskInfo(taskInfo => ({
          ...taskInfo,
          status: uiStatus,
          task_index: ruleIndex,
          task_name: ruleId
        }));
        biLogger.taskExpandCollapse({
          elementCount,
          failedElementCount,
          initiatedByUser,
          taskInfo: {
            ...currentTaskInfo,
            page_type: getPageTypeForBi(itemType),
            status: uiStatus,
            task_index: ruleIndex,
            task_name: ruleId
          },
          toggleType
        });
        if (toggleType === ToggleType.Expand) {
          navigateToPage(pageId);
        }
      },
      onStatusChange: () => {
        setCurrentTaskInfo(taskInfo => ({
          ...taskInfo,
          status: uiStatus
        }));
        biLogger.taskStatusChange({
          elementCount,
          failedElementCount,
          status: uiStatus
        });
      },
      parentGroupId: pageId,
      ruleId,
      status: uiStatus
    };
  }, [getRuleState, getGroupData, t, setRuleState, editorHost, a11yWizardAdapter, withErrorHandler, httpClient, branchId, getErrorPageAutoDomOrderModalProps, openSelectedArticle, handleElementClick, navigateToPage, biLogger, setCurrentTaskInfo, currentTaskInfo]);
  return {
    getPageAutoDomOrderRule
  };
};
//# sourceMappingURL=use-page-auto-dom-order-rule.js.map